import { c as _c } from "react/compiler-runtime";
import type { CartItemOutputDto, ItemPriceResultDto } from "@sticker-giant/sg-frontend-common";
import { useEffect, useRef } from "react";
import { trackRollQuantityReset } from "@/lib/analytics/segment";
import type { useUpdateItemQuantity } from "@/mutations/useUpdateItemQuantity";
import { useNewCartStore } from "@/stores/useNewCartStore";
import { getRollDriver } from "@/utils/rollQuantityUtils";
import { reconcileRollSelection } from "./cartPackagingUtils";
interface Props {
  item: CartItemOutputDto;
  pricingData?: ItemPriceResultDto;
  updateQuantity: ReturnType<typeof useUpdateItemQuantity>;
  isDetailsOpen: boolean;
  setShowWarning: (show: boolean) => void;
  setIsDetailsOpen: (open: boolean) => void;
}

/** Writes nulls for stored roll values that no longer match an option, surfaced like a quantity change. */
export function useReconcileRollSelection(t0) {
  const $ = _c(10);
  const {
    item,
    pricingData,
    updateQuantity,
    isDetailsOpen,
    setShowWarning,
    setIsDetailsOpen
  } = t0;
  const markRollSelectionLost = useNewCartStore(_temp);
  const clearRollSelectionLost = useNewCartStore(_temp2);
  const reconciledKeyRef = useRef(undefined);
  let t1;
  let t2;
  if ($[0] !== clearRollSelectionLost || $[1] !== isDetailsOpen || $[2] !== item || $[3] !== markRollSelectionLost || $[4] !== pricingData || $[5] !== setIsDetailsOpen || $[6] !== setShowWarning || $[7] !== updateQuantity) {
    t1 = () => {
      if (updateQuantity.isPending) {
        return;
      }
      const reconciliation = reconcileRollSelection(item, pricingData);
      if (!reconciliation) {
        return;
      }
      const rollDriver = getRollDriver(item);
      const key = [item.id, item.quantity, item.rollCount, item.rollQty, rollDriver].join(":");
      if (reconciledKeyRef.current === key) {
        return;
      }
      reconciledKeyRef.current = key;
      const {
        lost
      } = reconciliation;
      if (lost) {
        markRollSelectionLost(item.id);
        setShowWarning(true);
        setIsDetailsOpen(true);
      }
      updateQuantity.mutate({
        ...item,
        ...reconciliation.packaging,
        rollDriver
      }, {
        onSuccess: () => {
          if (lost) {
            trackRollQuantityReset("cart");
          }
        },
        onError: () => {
          if (!lost) {
            return;
          }
          clearRollSelectionLost(item.id);
          setShowWarning(false);
          setIsDetailsOpen(isDetailsOpen);
        }
      });
    };
    t2 = [item, pricingData, updateQuantity, isDetailsOpen, markRollSelectionLost, clearRollSelectionLost, setShowWarning, setIsDetailsOpen];
    $[0] = clearRollSelectionLost;
    $[1] = isDetailsOpen;
    $[2] = item;
    $[3] = markRollSelectionLost;
    $[4] = pricingData;
    $[5] = setIsDetailsOpen;
    $[6] = setShowWarning;
    $[7] = updateQuantity;
    $[8] = t1;
    $[9] = t2;
  } else {
    t1 = $[8];
    t2 = $[9];
  }
  useEffect(t1, t2);
}
function _temp2(state_0) {
  return state_0.clearRollSelectionLost;
}
function _temp(state) {
  return state.markRollSelectionLost;
}